import { 
  SiteTask, 
  CreateSiteTaskRequest, 
  UpdateSiteTaskRequest,
  TaskCompletion,
  CompleteTaskRequest,
  User
} from './types';
import { PhotoRequirement } from '../../types';
import { useTaskCompletions } from '../../contexts/TaskCompletionsContext';

// Create a singleton instance to hold the context reference
let taskCompletionsContextRef: ReturnType<typeof useTaskCompletions> | null = null;

// Function to set the context reference
export const setTaskCompletionsContext = (context: ReturnType<typeof useTaskCompletions>) => {
  taskCompletionsContextRef = context;
};

/**
 * Mock implementation for site task management
 */
export class SiteTasksMockService {
  // Mock site tasks for development
  private mockSiteTasks: Record<string, SiteTask[]> = {};
  
  // Mock task completions for development
  private mockTaskCompletions: Record<string, TaskCompletion[]> = {};
  
  // Track active status for mock task completions
  private mockActiveCompletions: Record<string, boolean> = {};

  /**
   * Get mock site tasks
   */
  getSiteTasks(siteId: string): SiteTask[] {
    console.log('Mock tasks for this site:', this.mockSiteTasks[siteId] || []);
    return this.mockSiteTasks[siteId] || [];
  }

  /**
   * Get mock site task by ID
   */
  getSiteTaskById(siteId: string, taskId: string): SiteTask {
    // Find the task in mock data
    const tasks = this.mockSiteTasks[siteId] || [];
    const task = tasks.find(t => t.id === taskId);
    
    if (task) {
      return { ...task };
    }
    
    throw new Error('Task not found');
  }

  /**
   * Create a mock site task
   */
  createSiteTask(siteId: string, taskData: CreateSiteTaskRequest): SiteTask {
    console.log('Using mock site task creation for siteId:', siteId);
    
    // Initialize the site's tasks array if it doesn't exist
    if (!this.mockSiteTasks[siteId]) {
      this.mockSiteTasks[siteId] = [];
      console.log('Initialized empty tasks array for site:', siteId);
    }
    
    // Find the Check Out task (case-insensitive)
    const tasks = this.mockSiteTasks[siteId];
    console.log('DEBUG: All task locations:', tasks.map(t => `'${t.taskLocation}'`).join(', '));
    
    const checkOutTask = tasks.find(t => 
      t.taskLocation.toLowerCase() === "check out".toLowerCase()
    );
    
    console.log('DEBUG: Check Out task found:', checkOutTask ? 'Yes' : 'No');
    if (checkOutTask) {
      console.log('DEBUG: Check Out task details:', {
        id: checkOutTask.id,
        location: checkOutTask.taskLocation,
        orderNumber: checkOutTask.orderNumber
      });
    }
    
    let newOrderNumber;
    
    if (checkOutTask) {
      // Use the Check Out task's order number for the new task
      newOrderNumber = checkOutTask.orderNumber;
      
      // Increment the Check Out task's order number
      checkOutTask.orderNumber += 1;
      console.log('Incremented Check Out task order number to:', checkOutTask.orderNumber);
    } else {
      // If no Check Out task exists, use the highest order number + 1
      newOrderNumber = this.getNextOrderNumber(siteId);
      console.log('No Check Out task found, using next order number:', newOrderNumber);
    }
    
    // Create a new task with a generated ID
    const newTask: SiteTask = {
      id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      taskLocation: taskData.taskLocation,
      qrCode: taskData.qrCode,
      instructions: taskData.instructions,
      frequency: taskData.frequency || '1 time',
      intervalHours: taskData.intervalHours,
      orderNumber: taskData.orderNumber || newOrderNumber,
      photoRequirement: taskData.photoRequirement || PhotoRequirement.Required,
      siteId
    };
    
    console.log('Created new mock task:', newTask);
    
    // Add to mock tasks
    this.mockSiteTasks[siteId].push(newTask);
    console.log('Added task to mockSiteTasks. Current tasks for site:', this.mockSiteTasks[siteId]);
    
    return { ...newTask };
  }

  /**
   * Update a mock site task
   */
  updateSiteTask(siteId: string, taskId: string, taskData: UpdateSiteTaskRequest): boolean {
    console.log('Using mock site task update for siteId:', siteId, 'taskId:', taskId);
    
    // Find the task to update
    const tasks = this.mockSiteTasks[siteId] || [];
    const taskIndex = tasks.findIndex(t => t.id === taskId);
    
    if (taskIndex === -1) {
      throw new Error('Task not found');
    }
    
    // Update task data
    this.mockSiteTasks[siteId][taskIndex] = {
      ...this.mockSiteTasks[siteId][taskIndex],
      ...taskData
    };
    
    return true;
  }

  /**
   * Delete a mock site task
   */
  deleteSiteTask(siteId: string, taskId: string): boolean {
    console.log('Using mock site task deletion for siteId:', siteId, 'taskId:', taskId);
    
    // Find the task to delete
    const tasks = this.mockSiteTasks[siteId] || [];
    const taskIndex = tasks.findIndex(t => t.id === taskId);
    
    if (taskIndex === -1) {
      throw new Error('Task not found');
    }
    
    // Remove from mock tasks
    this.mockSiteTasks[siteId].splice(taskIndex, 1);
    
    return true;
  }

  /**
   * Complete a mock site task
   */
  completeTask(taskId: string, completionData: CompleteTaskRequest): TaskCompletion {
    console.log('Using mock task completion for taskId:', taskId);
    
    // Use mock implementation
    const completion = this.createMockTaskCompletion(taskId, completionData);
    
    // Ensure the completion is added to the context
    if (taskCompletionsContextRef) {
      console.log('Adding task completion to context from completeTask:', completion.id);
      taskCompletionsContextRef.addTaskCompletion(taskId, completion);
    } else {
      console.warn('TaskCompletionsContext not available in completeTask');
    }
    
    return completion;
  }
  
  /**
   * Create a mock task completion
   */
  createMockTaskCompletion(taskId: string, completionData: CompleteTaskRequest, userId?: string): TaskCompletion {
    // Get the current user from the request data or from the parameter
    let userIdToUse = userId;
    
    // If userId is provided in completionData, use it (CompleteTaskRequest now has optional userId)
    if (!userIdToUse && completionData.userId) {
      userIdToUse = completionData.userId;
    }
    
    // If still no userId, try to get from localStorage as a fallback
    if (!userIdToUse) {
      try {
        // Try localStorage first
        const currentUserJson = localStorage.getItem('user');
        if (currentUserJson) {
          const currentUser = JSON.parse(currentUserJson) as User;
          userIdToUse = currentUser.id;
        } else {
          // Fallback to sessionStorage for backward compatibility
          const sessionUserJson = sessionStorage.getItem('user');
          if (sessionUserJson) {
            const sessionUser = JSON.parse(sessionUserJson) as User;
            userIdToUse = sessionUser.id;
          } else {
            userIdToUse = localStorage.getItem('userId') || sessionStorage.getItem('userId') || '';
          }
        }
      } catch (error) {
        console.error('Error getting user ID from storage:', error);
      }
    }
    
    if (!userIdToUse) {
      console.error('No user ID provided in completion data or parameter');
      throw new Error('User ID is required');
    }
    
    // Create a new completion with a generated ID
    const newCompletion: TaskCompletion = {
      id: `completion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      notes: completionData.notes,
      imageUrl: completionData.imageUrl,
      latitude: completionData.latitude,
      longitude: completionData.longitude,
      completedAt: new Date().toISOString(),
      siteTaskId: taskId,
      userId: userIdToUse
    };
    
    // Use the context if available
    if (taskCompletionsContextRef) {
      taskCompletionsContextRef.addTaskCompletion(taskId, newCompletion);
    } else {
      // Fallback to in-memory storage if context is not available
      // Initialize the task's completions array if it doesn't exist
      if (!this.mockTaskCompletions[taskId]) {
        this.mockTaskCompletions[taskId] = [];
      }
      
      // Add to mock completions
      this.mockTaskCompletions[taskId].push(newCompletion);
      
      // Set initial active status to true
      this.mockActiveCompletions[newCompletion.id] = true;
      
      console.log('Stored mock completion in memory (context not available):', newCompletion);
    }
    
    return { ...newCompletion };
  }

  /**
   * Get the next available order number for a site
   */
  getNextOrderNumber(siteId: string): number {
    const tasks = this.mockSiteTasks[siteId] || [];
    if (tasks.length === 0) {
      return 1;
    }
    
    const highestOrderNumber = Math.max(...tasks.map(t => t.orderNumber));
    return highestOrderNumber + 1;
  }

  /**
   * Reset completions for a specific task
   */
  resetTaskCompletions(taskId: string, userId?: string): boolean {
    console.log('Using mock task completion reset for taskId:', taskId);
    
    // Use mock implementation
    return this.resetMockTaskCompletions(taskId, userId);
  }
  
  /**
   * Reset all completions for a site
   */
  resetAllTaskCompletions(siteId: string, userId?: string): boolean {
    console.log('Using mock site task completions reset for siteId:', siteId);
    
    // Use mock implementation
    return this.resetAllMockTaskCompletions(siteId, userId);
  }
  
  /**
   * Reset mock completions for a specific task
   */
  resetMockTaskCompletions(taskId: string, userId?: string): boolean {
    // If userId is not provided, try to get it from the request
    if (!userId) {
      // Try to get from localStorage as a fallback
      try {
        // Try localStorage first
        const currentUserJson = localStorage.getItem('user');
        if (currentUserJson) {
          const currentUser = JSON.parse(currentUserJson) as User;
          userId = currentUser.id;
        } else {
          // Fallback to sessionStorage for backward compatibility
          const sessionUserJson = sessionStorage.getItem('user');
          if (sessionUserJson) {
            const sessionUser = JSON.parse(sessionUserJson) as User;
            userId = sessionUser.id;
          }
        }
      } catch (error) {
        console.error('Error getting user ID:', error);
      }
      
      if (!userId) {
        console.error('No user ID provided for resetMockTaskCompletions');
        return false;
      }
    }
    
    const currentUserId = userId;
    
    // Use the context if available
    if (taskCompletionsContextRef) {
      return taskCompletionsContextRef.resetTaskCompletions(taskId, currentUserId);
    }
    
    // Fallback to in-memory storage if context is not available
    // Mark all completions for this task as inactive in memory
    const completions = this.mockTaskCompletions[taskId] || [];
    for (const completion of completions) {
      // Only reset completions for the current user
      if (completion.userId === currentUserId) {
        this.mockActiveCompletions[completion.id] = false;
      }
    }
    
    console.log(`Reset completions for task ${taskId} and user ${currentUserId} in memory (context not available)`);
    return true;
  }
  
  /**
   * Reset all mock completions for a site
   */
  resetAllMockTaskCompletions(siteId: string, userId?: string): boolean {
    // If userId is not provided, try to get it from the request
    if (!userId) {
      // Try to get from localStorage as a fallback
      try {
        // Try localStorage first
        const currentUserJson = localStorage.getItem('user');
        if (currentUserJson) {
          const currentUser = JSON.parse(currentUserJson) as User;
          userId = currentUser.id;
        } else {
          // Fallback to sessionStorage for backward compatibility
          const sessionUserJson = sessionStorage.getItem('user');
          if (sessionUserJson) {
            const sessionUser = JSON.parse(sessionUserJson) as User;
            userId = sessionUser.id;
          }
        }
      } catch (error) {
        console.error('Error getting user ID:', error);
      }
      
      if (!userId) {
        console.error('No user ID provided for resetAllMockTaskCompletions');
        return false;
      }
    }
    
    const currentUserId = userId;
    
    // Get all tasks for this site
    const tasks = this.mockSiteTasks[siteId] || [];
    
    // Use the context if available
    if (taskCompletionsContextRef) {
      const taskIds = tasks.map(task => task.id);
      return taskCompletionsContextRef.resetAllTaskCompletions(siteId, currentUserId, taskIds);
    }
    
    // Fallback to in-memory storage if context is not available
    // Reset completions for each task
    for (const task of tasks) {
      this.resetMockTaskCompletions(task.id, currentUserId);
    }
    
    console.log(`Reset all completions for site ${siteId} and user ${currentUserId} in memory (context not available)`);
    return true;
  }
  
  /**
   * Reset all stored reports for a user
   */
  resetStoredReports(userId: string): void {
    try {
      // Update stored reports in localStorage
      const storedReportsJson = localStorage.getItem('submittedReports');
      if (storedReportsJson) {
        const storedReports = JSON.parse(storedReportsJson);
        let updated = false;
        
        // Mark all reports by this user as inactive
        for (const report of storedReports) {
          if (report.userId === userId || report.guardId === userId) {
            report.isActive = false;
            updated = true;
          }
        }
        
        if (updated) {
          localStorage.setItem('submittedReports', JSON.stringify(storedReports));
          console.log(`Reset all stored reports for user ${userId}`);
        }
      }
    } catch (error) {
      console.error('Error resetting stored reports:', error);
    }
  }
  
  /**
   * Clear all task completions
   */
  clearAllTaskCompletions(): boolean {
    try {
      // Use the context if available
      if (taskCompletionsContextRef) {
        return taskCompletionsContextRef.clearAllTaskCompletions();
      }
      
      // Fallback to in-memory storage if context is not available
      // Clear in-memory completions
      this.mockTaskCompletions = {};
      this.mockActiveCompletions = {};
      
      console.log('Cleared all task completions from memory (context not available)');
      return true;
    } catch (error) {
      console.error('Error clearing task completions:', error);
      return false;
    }
  }
  
  /**
   * Get mock task completions, filtering by active status
   */
  getTaskCompletions(taskId: string): TaskCompletion[] {
    if (!taskCompletionsContextRef) {
      console.error('Task completions context not set in getTaskCompletions');
      
      // Fallback to in-memory completions if context is not available
      const inMemoryCompletions = this.mockTaskCompletions[taskId] || [];
      return inMemoryCompletions.filter(completion => {
        if (this.mockActiveCompletions[completion.id] !== undefined) {
          return this.mockActiveCompletions[completion.id];
        }
        return true;
      });
    }
    
    // Get completions from the context
    const contextCompletions = taskCompletionsContextRef.getTaskCompletions(taskId);
    console.log(`Getting task completions from context for task ${taskId}:`, contextCompletions.length);
    
    return contextCompletions;
  }
}

// Create a default instance
export const siteTasksMockService = new SiteTasksMockService();

export default siteTasksMockService;
